import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { ArrowRight, Brain, Users, Globe, Mic, Calendar, Clock } from 'lucide-react';

const KEYNOTE_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/lvlgfpli_IMG_9913.jpg";
const ALL_LOGOS_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/ont6xb82_All%20logos.png";

const Counter = ({ target, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 2000;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

export default function KeynotesPage() {
  const topicsRef = useRef(null);
  const isTopicsInView = useInView(topicsRef, { once: true, margin: "-100px" });
  const includesRef = useRef(null);
  const isIncludesInView = useInView(includesRef, { once: true, margin: "-100px" });

  const topics = [
    {
      icon: Brain,
      title: "Signal Over Noise",
      description: "How to engineer clarity in an age of distraction. Learn the frameworks that elite performers use to filter information and make better decisions.",
    },
    {
      icon: Users,
      title: "Psychological Safety & Performance",
      description: "Build teams that take intelligent risks, speak up, and perform at their best. The science behind high-performing cultures.",
    },
    {
      icon: Mic,
      title: "Peak Performance with Well-Being",
      description: "Sustainable excellence without burnout. The methodology used by Olympic athletes and Fortune 500 leaders.",
    },
  ];

  const keynoteIncludes = [
    { icon: Calendar, text: "Pre-engagement discovery call" },
    { icon: Clock, text: "45-90 minute keynote session" },
    { icon: Mic, text: "Choose between signature or custom topics" },
    { icon: Globe, text: "14 days recording access available" },
  ];

  return (
    <main data-testid="keynotes-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] lg:min-h-[80vh] flex items-center bg-[#1F2A44] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={KEYNOTE_IMAGE}
            alt="Shayamal delivering keynote"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F2A44] via-[#1F2A44]/80 to-transparent" />
        </div>

        <div className="container-peak relative z-10 py-20 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0 px-4 sm:px-0"
          >
            <span className="section-tag block mb-4 lg:mb-6">Keynotes</span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-light leading-[1.1] mb-4 lg:mb-6 text-[#F4F1EC]">
              Inspire your audience to{' '}
              <em className="not-italic text-[#D97904]">perform at their peak</em>
            </h1>
            <p className="text-[#F4F1EC]/70 text-sm lg:text-base xl:text-lg mb-6 lg:mb-10">
              High-impact talks that transform how your audience thinks, performs, and leads.
            </p>
            <a
              href="#request"
              className="btn-primary inline-flex items-center gap-3"
            >
              Book a Keynote
              <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Stats Section with Icons */}
      <section className="py-10 lg:py-16 bg-[#F4F1EC] border-b border-[#E6E1DA]">
        <div className="container-peak">
          <div className="grid grid-cols-3 gap-4 lg:gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-10 h-10 lg:w-12 lg:h-12 mx-auto mb-2 lg:mb-3 flex items-center justify-center bg-[#D97904]/10 rounded-full">
                <Mic className="w-4 h-4 lg:w-5 lg:h-5 text-[#D97904]" />
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-['DM_Sans'] font-light text-[#D97904]">
                <Counter target={1000} suffix="+" />
              </div>
              <p className="text-[#5A6B7A] text-[10px] lg:text-sm mt-0.5 lg:mt-1">Keynotes Delivered</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 lg:w-12 lg:h-12 mx-auto mb-2 lg:mb-3 flex items-center justify-center bg-[#D97904]/10 rounded-full">
                <Globe className="w-4 h-4 lg:w-5 lg:h-5 text-[#D97904]" />
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-['DM_Sans'] font-light text-[#D97904]">
                18
              </div>
              <p className="text-[#5A6B7A] text-[10px] lg:text-sm mt-0.5 lg:mt-1">Countries</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 lg:w-12 lg:h-12 mx-auto mb-2 lg:mb-3 flex items-center justify-center bg-[#D97904]/10 rounded-full">
                <Users className="w-4 h-4 lg:w-5 lg:h-5 text-[#D97904]" />
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl font-['DM_Sans'] font-light text-[#D97904]">
                <Counter target={50000} suffix="+" />
              </div>
              <p className="text-[#5A6B7A] text-[10px] lg:text-sm mt-0.5 lg:mt-1">Lives Impacted</p>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Topics */}
      <section ref={topicsRef} className="py-14 lg:py-24 bg-[#F4F1EC]">
        <div className="container-peak">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isTopicsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10 lg:mb-16"
          >
            <span className="section-tag block mb-4 lg:mb-6">Signature Topics</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#1F2A44]">
              Talks that drive <em className="not-italic text-[#D97904]">real change</em>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 lg:gap-8 max-w-6xl mx-auto">
            {topics.map((topic, index) => (
              <motion.div
                key={topic.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isTopicsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-white p-5 lg:p-8 border border-[#E6E1DA] hover:border-[#D97904] transition-all card-hover"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center bg-[#F4F1EC] mb-4 lg:mb-6">
                  <topic.icon className="w-4 h-4 lg:w-5 lg:h-5 text-[#D97904]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-lg lg:text-2xl text-[#1F2A44] mb-2 lg:mb-4">
                  {topic.title}
                </h3>
                <p className="text-[#5A6B7A] text-xs lg:text-sm leading-relaxed">
                  {topic.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8 lg:mt-12">
            <p className="text-[#5A6B7A] text-sm mb-4">Have a specific topic in mind?</p>
            <a href="#request" className="text-[#D97904] font-['Syne'] text-xs uppercase tracking-wider hover:underline inline-flex items-center gap-2">
              Request a Custom Keynote <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section ref={includesRef} className="py-16 lg:py-24 bg-white">
        <div className="container-peak">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isIncludesInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <span className="section-tag block mb-4 lg:mb-6">What's Included</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] mb-4 lg:mb-6 text-[#1F2A44]">
                A standard keynote <em className="not-italic text-[#D97904]">includes</em>
              </h2>
              <p className="text-[#5A6B7A] text-sm lg:text-base mb-6">
                Every engagement is designed for maximum impact.
              </p>
              <a href="#request" className="btn-primary inline-flex items-center gap-3">
                Request a Keynote
                <ArrowRight size={16} />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isIncludesInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="bg-[#F4F1EC] p-6 lg:p-8"
            >
              <div className="space-y-4 lg:space-y-5">
                {keynoteIncludes.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-white border border-[#E6E1DA] flex-shrink-0">
                      <item.icon className="w-4 h-4 text-[#D97904]" />
                    </div>
                    <span className="text-[#1F2A44] text-sm lg:text-base pt-2">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Logos - Same as homepage */}
      <section className="py-10 lg:py-14 bg-[#F4F1EC] border-y border-[#E6E1DA]">
        <div className="container-peak">
          <p className="text-center text-[10px] lg:text-[11px] tracking-[0.3em] uppercase text-[#5A6B7A] mb-6 lg:mb-8 font-['Syne']">
            Trusted by World-Class Organizations
          </p>
          <div className="max-w-4xl mx-auto">
            <img
              src={ALL_LOGOS_IMAGE}
              alt="Our clients"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="request" className="py-16 lg:py-24 bg-[#1F2A44]">
        <div className="container-peak">
          <div className="max-w-2xl mx-auto text-center">
            <span className="section-tag block mb-4 lg:mb-6">Book a Keynote</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] mb-4 lg:mb-6 text-[#F4F1EC]">
              Let's create an unforgettable experience
            </h2>
            <p className="text-[#F4F1EC]/70 text-sm lg:text-base mb-8 lg:mb-10">
              Tell us about your event and we'll craft a keynote that resonates with your audience.
            </p>
            <a
              href="https://calendar.app.google/evsn9LsubajagFF3A"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3"
            >
              Book a Call
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
